/*
Copyright 2024-2025 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  ProcessorWrapper,
  supportsBackgroundProcessors,
  type BackgroundOptions,
} from "@livekit/track-processors";
import {
  createContext,
  type FC,
  type JSX,
  use,
  useEffect,
  useMemo,
} from "react";
import { type LocalVideoTrack } from "livekit-client";
import { logger } from "matrix-js-sdk/lib/logger";

import {
  backgroundBlur as backgroundBlurSettings,
  backgroundImage as backgroundImageSetting,
  useSetting,
} from "../settings/settings";
import { BlurBackgroundTransformer } from "./BlurBackgroundTransformer";
import { backgroundBlurRadii } from "./backgroundBlur";
import { useBackgroundImageUrl } from "./useBackgroundImageUrl";
import { useInitial } from "../useInitial";

type ProcessorState = {
  supported: boolean | undefined;
  processor: undefined | ProcessorWrapper<BackgroundOptions>;
};

const ProcessorContext = createContext<ProcessorState | undefined>(undefined);

export function useTrackProcessor(): ProcessorState {
  const state = use(ProcessorContext);
  if (state === undefined)
    throw new Error(
      "useTrackProcessor must be used within a ProcessorProvider",
    );
  return state;
}

export const useTrackProcessorSync = (
  videoTrack: LocalVideoTrack | null,
): void => {
  const { processor } = useTrackProcessor();
  useEffect(() => {
    if (!videoTrack) return;
    if (processor && !videoTrack.getProcessor()) {
      void videoTrack.setProcessor(processor);
    }
    if (!processor && videoTrack.getProcessor()) {
      void videoTrack.stopProcessor();
    }
  }, [processor, videoTrack]);
};

interface Props {
  children: JSX.Element;
}

export const ProcessorProvider: FC<Props> = ({ children }) => {
  // The setting the user wants to have
  const [blurLevel] = useSetting(backgroundBlurSettings);
  const [imageChoice] = useSetting(backgroundImageSetting);
  const imageUrl = useBackgroundImageUrl(imageChoice);
  const supported = useMemo(() => supportsBackgroundProcessors(), []);

  // Размытие и картинка — один и тот же обработчик кадров: он в любом случае
  // отделяет человека от фона, а рисует за ним либо размытую копию кадра, либо
  // картинку. Заводить второй значило бы держать вторую модель распознавания.
  const processor = useInitial(
    () =>
      new ProcessorWrapper(
        new BlurBackgroundTransformer(
          // Начальное состояние берём то, которое человек выбрал в прошлый раз.
          // Поставить сюда размытие «на всякий случай» нельзя: обработчик
          // подключается к камере позже, чем срабатывает эффект ниже, и при
          // выбранной картинке первые кадры ушли бы размытыми.
          blurLevel === "off"
            ? {}
            : { blurRadius: backgroundBlurRadii[blurLevel] },
        ),
        "background-effect",
      ),
  );

  // Ступень применяем через updateTransformerOptions, а не пересозданием
  // обработчика: библиотека при этом меняет только параметр шейдера и не
  // трогает уже открытую камеру и сегментацию. Пересборка ProcessorWrapper
  // заново поднимала бы модель распознавания на каждое переключение ступени.
  useEffect(() => {
    if (blurLevel !== "off") {
      processor
        .updateTransformerOptions({
          blurRadius: backgroundBlurRadii[blurLevel],
        })
        .catch((e) =>
          logger.error("Failed to update the background blur radius", e),
        );
      return;
    }

    if (imageUrl !== null) {
      processor
        .updateTransformerOptions({ imagePath: imageUrl })
        .catch((e) => logger.error("Не удалось поставить картинку фона", e));
    }
  }, [processor, blurLevel, imageUrl]);

  // This is the actual state exposed through the context
  const active = blurLevel !== "off" || imageUrl !== null;
  const processorState = useMemo(
    () => ({
      supported,
      processor: supported && active ? processor : undefined,
    }),
    [supported, active, processor],
  );

  return <ProcessorContext value={processorState}>{children}</ProcessorContext>;
};
